import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Building2, Lock, ArrowRight, ArrowLeft, ShieldCheck, Mail, Phone } from "lucide-react";
import { alpha, mix, readableOn, themeOf, heroSrcOf, safeAccent } from "@/lib/portalTheme";
import useWhiteLabelHead from "@/lib/useWhiteLabelHead";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LABEL = "text-[10.5px] font-bold uppercase tracking-[0.16em]";

/** Where a partner's clients sign in: their adviser's own address, their
 *  adviser's own colours. Nothing of ours appears on this page. */
export default function PartnerClientLogin() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { login, logout } = useAuth();
  const [portal, setPortal] = useState(null);
  const [gone, setGone] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    axios.get(`${API}/public/partner/${slug}`)
      .then(({ data }) => setPortal(data))
      .catch(() => setGone(true));
  }, [slug]);

  useWhiteLabelHead(portal);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const res = await login(email.trim(), password);
      if (res?.requiresOtp || res?.user?.role !== "partner_client") {
        await logout();
        setError("Those details don't open a statement here. Check with your adviser.");
        setBusy(false);
        return;
      }
      // A client of another firm must not be able to sign in on this page.
      const { data } = await api.get("/client/me");
      if (data?.brand?.slug && data.brand.slug !== slug) {
        await logout();
        setError("Those details don't open a statement here. Check with your adviser.");
        setBusy(false);
        return;
      }
      navigate(`/p/${slug}/portal`, { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "We couldn't sign you in with those details.");
      setBusy(false);
    }
  };

  if (gone) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6" data-testid="client-login-not-found">
        <div className="glass-strong p-10 text-center max-w-md">
          <Building2 size={26} className="mx-auto mb-3 text-ink-3" />
          <div className="font-display text-xl font-semibold">This sign-in page isn't available</div>
          <p className="text-sm text-ink-2 mt-2">Check the link you were given, or ask your adviser for a fresh one.</p>
        </div>
      </div>
    );
  }
  if (!portal) return <div className="min-h-screen flex items-center justify-center text-sm text-ink-2" data-testid="client-login-loading">Loading…</div>;

  const accent = safeAccent(portal.accent || "#4060FF", portal.theme);
  const t = themeOf(portal.theme);
  const onAccent = readableOn(accent);
  const heroSrc = heroSrcOf(portal);
  const fieldStyle = { background: t.inputBg, border: `1.5px solid ${t.border}`, color: t.ink };

  return (
    <div className="min-h-screen grid lg:grid-cols-[minmax(0,1fr)_480px]"
      style={{ background: t.pageBg, color: t.ink }} data-testid="client-login-page">
      {/* the firm's side */}
      <div className="relative hidden lg:flex flex-col justify-between p-12"
        style={{ background: `linear-gradient(120deg, ${mix(accent, "#000000", 0.34)} 0%, ${accent} 70%)` }}>
        {heroSrc && (
          <>
            <img src={heroSrc} alt="" className="absolute inset-0 w-full h-full object-cover opacity-30" />
            <div className="absolute inset-0" style={{ background: `linear-gradient(120deg, ${alpha(mix(accent, "#000000", 0.4), 0.92)} 0%, ${alpha(accent, 0.72)} 100%)` }} />
          </>
        )}
        <div className="relative flex items-center gap-3">
          {portal.logo
            ? <img src={portal.logo} alt="" style={{ height: 36, objectFit: "contain" }} data-testid="client-login-logo" />
            : <span className="w-11 h-11 rounded-2xl flex items-center justify-center" style={{ background: alpha(onAccent === "#FFFFFF" ? "#FFFFFF" : "#0F1226", 0.22) }}><Building2 size={19} color={onAccent} /></span>}
          <span className="font-display text-xl font-semibold tracking-tight" style={{ color: onAccent }}>{portal.name}</span>
        </div>
        <div className="relative max-w-[440px]">
          <h1 className="font-display text-4xl font-extrabold leading-[1.1] tracking-tight" style={{ color: onAccent }}>
            Your statement, whenever you want it.
          </h1>
          <p className="text-[15px] leading-relaxed mt-4" style={{ color: alpha(onAccent === "#FFFFFF" ? "#FFFFFF" : "#0F1226", 0.86) }}>
            Capital placed, return accrued, what has been paid and what is still running — month by month, and a
            PDF you can keep.
          </p>
        </div>
        <div className="relative flex items-center gap-2 text-[12.5px]" style={{ color: alpha(onAccent === "#FFFFFF" ? "#FFFFFF" : "#0F1226", 0.8) }}>
          <ShieldCheck size={14} /> Private to you · {portal.name}
        </div>
      </div>

      {/* the form */}
      <div className="flex flex-col justify-center px-6 sm:px-10 py-12">
        <div className="lg:hidden flex items-center gap-3 mb-8">
          {portal.logo
            ? <img src={portal.logo} alt="" style={{ height: 30, objectFit: "contain" }} />
            : <span className="w-10 h-10 rounded-2xl flex items-center justify-center" style={{ background: alpha(accent, 0.14), color: accent }}><Building2 size={18} /></span>}
          <span className="font-display text-lg font-semibold tracking-tight" style={{ color: t.ink }}>{portal.name}</span>
        </div>

        <div className={LABEL} style={{ color: t.ink3 }}>Client sign-in</div>
        <h2 className="font-display text-3xl font-bold tracking-tight mt-2" style={{ color: t.ink }}>Open my statement</h2>
        <p className="text-[13.5px] mt-2" style={{ color: t.ink2 }}>
          Use the email and password {portal.name} gave you.
        </p>

        <form onSubmit={submit} className="mt-7 space-y-3.5">
          <div>
            <label className={`${LABEL} block mb-1.5`} style={{ color: t.ink3 }}>Email</label>
            <input type="email" required value={email} autoFocus autoComplete="email"
              onChange={(e) => { setEmail(e.target.value); setError(""); }}
              className="w-full px-3.5 py-3 rounded-xl outline-none text-sm"
              style={fieldStyle} data-testid="client-login-email" />
          </div>
          <div>
            <label className={`${LABEL} block mb-1.5`} style={{ color: t.ink3 }}>Password</label>
            <input type="password" required value={password} autoComplete="current-password"
              onChange={(e) => { setPassword(e.target.value); setError(""); }}
              className="w-full px-3.5 py-3 rounded-xl outline-none text-sm"
              style={fieldStyle} data-testid="client-login-password" />
          </div>
          {error && (
            <div className="text-[12.5px] px-3.5 py-2.5 rounded-xl"
              style={{ background: alpha("#E11D48", 0.1), color: "#E11D48" }} data-testid="client-login-error">
              {error}
            </div>
          )}
          <button type="submit" disabled={busy || !email.trim() || !password}
            className="w-full rounded-xl px-5 py-3.5 text-[13.5px] font-bold inline-flex items-center justify-center gap-2 transition-transform hover:-translate-y-0.5 disabled:opacity-50"
            style={{ background: accent, color: onAccent, boxShadow: `0 12px 28px ${alpha(accent, 0.3)}` }}
            data-testid="client-login-submit">
            {busy ? "Signing you in…" : <>Sign in <ArrowRight size={15} /></>}
          </button>
        </form>

        <div className="mt-6 rounded-2xl px-4 py-3.5" style={{ background: t.band, border: `1px solid ${t.border}` }}>
          <div className="flex items-start gap-2.5 text-[12.5px]" style={{ color: t.ink2 }}>
            <Lock size={13} className="mt-0.5 shrink-0" style={{ color: accent }} />
            <span>
              Forgotten your password? {portal.name} can set you a new one — just get in touch.
              <span className="block mt-1.5 space-x-3">
                {portal.contact_email && (
                  <a href={`mailto:${portal.contact_email}`} className="inline-flex items-center gap-1.5 hover:underline" data-testid="client-login-contact-email">
                    <Mail size={12} style={{ color: accent }} /> {portal.contact_email}
                  </a>
                )}
                {portal.contact_phone && (
                  <a href={`tel:${portal.contact_phone.replace(/[^\d+]/g, "")}`} className="inline-flex items-center gap-1.5 hover:underline">
                    <Phone size={12} style={{ color: accent }} /> {portal.contact_phone}
                  </a>
                )}
              </span>
            </span>
          </div>
        </div>

        <a href={`/p/${slug}`} className="mt-6 text-[12.5px] inline-flex items-center gap-1.5 hover:underline"
          style={{ color: t.ink3 }} data-testid="client-login-back">
          <ArrowLeft size={13} /> Back to {portal.name}
        </a>
      </div>
    </div>
  );
}
